/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import { Group} from "../../models/group";
import {RoleManagerDialogComponent} from "./rolemanager-dialog.component";
import {UserGroupService} from "../../services/user-group.service";
import {RoleNotificationService} from "../../services/role-notification.service";
import {Subscription} from "rxjs";
import {GroupPermissions} from "../../models/group";
import {PagePermissionService} from "../../services/page-permission.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare var $: any;

@Component({
  selector: 'app-rolemanager',
  templateUrl: './rolemanager.component.html'
})
export class RoleManagerComponent implements OnInit, OnDestroy {
  // List of all the roles on the page
  roles: Group[] = null;

  // List of all the checkbox values on the page
  userRoleCheckbox: Map<string, string> = new Map();

  @ViewChild('roleManagerDialog') dialogComponent: RoleManagerDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  // Subscription to determine if we need to refresh the roles on the page
  refreshRolesSubscription: Subscription;

  constructor(private userGroupService: UserGroupService, private roleNotificationService: RoleNotificationService, private pagePermissionService: PagePermissionService) { }

  ngOnInit(): void {
    // Refresh the roles, then ask for a subscription to be notified when any roles are updated in the future
    this.refreshRoles();

    // Set up a subscription so that if something changes, we can refresh the roles
    this.refreshRolesSubscription = this.roleNotificationService.getRefreshRolesListener().subscribe(result => {
      this.refreshRoles();
    });
  }

  refreshRoles(): void {
    // Retrieve all the user groups
    this.userGroupService.getAllUserGroups().then(
      response => {
        this.roles = response;
      },
      error => {
        this.handleError(error);
      }
    )
  }

  ngOnDestroy(): void {
    // Remove the subscription
    if (this.refreshRolesSubscription != null) {
        this.refreshRolesSubscription.unsubscribe();
        this.refreshRolesSubscription = null;
    }
  }

  addNewRole(): void {
    // Create a new empty role with empty page permissions, object permissions and group permissions
    const userRole = new Group();
    userRole.pagePermissions = this.pagePermissionService.getEmptyPagePermissions();
    userRole.groupPermissions = new GroupPermissions();
    // userRole.objectPermissions = {};
    this.dialogComponent.userRole = userRole;
    $("#roleEditModal").show();
  }

  // Returns the ids associated with all checkboxes that are checked
  private getSelectedRoleIds(): number[] {
    const selectedRoleIds: number[] = [];
    for(const key of Object.keys(this.userRoleCheckbox)) {
      if (this.userRoleCheckbox[key]) {
        selectedRoleIds.push(Number(key));
      }
    }
    return selectedRoleIds;
  }

  duplicateUserRoles(): void {
    const selectedRoleIds = this.getSelectedRoleIds();

    for(const selectedRoleId of selectedRoleIds) {
      // Find the role we want to duplicate
      let roleToDuplicate = null;
      for(const role of this.roles) {
        if (role.id == selectedRoleId) {
          roleToDuplicate = role;
          break;
        }
      }

      // modify the role by removing the id, and changing the name by appending 'COPY' to it
      const duplicatedRole = JSON.parse(JSON.stringify(roleToDuplicate));
      delete duplicatedRole.id;
      duplicatedRole.name = duplicatedRole.name + " COPY";

      // create this new role
      this.userGroupService.createUserGroup(duplicatedRole).then(
        response => {
          console.log("role duplicated");
          this.refreshRoles();

        },
        error => {
          this.handleError(error);
        }
      );
    }

  }

  deleteUserRoleQuery(): void {
    // TODO: Need an 'ARE YOU SURE?' dialog before deleting roles
    this.deleteUserRoles();
  }

  deleteUserRoles(): void {
    const selectedRoleIds = this.getSelectedRoleIds();
    for(const selectedRoleId of selectedRoleIds) {
      // Find the role we want to delete
      let roleToDelete = null;
      for(const role of this.roles) {
        if (role.id == selectedRoleId) {
          roleToDelete = role;
          break;
        }
      }

      // delete this role
      this.userGroupService.deleteUserGroup(roleToDelete.id).then(
        response => {
          console.log("role deleted");
          this.refreshRoles();
        },
        error => {
          this.handleError(error);
        }
      )
    }

  }

  showRole(userRole: Group): void {
    // Show an existing user role
    this.dialogComponent.userRole = userRole;

    $("#roleEditModal").show();
  }

  handleError(error: any): void {
    console.error("DevicesComponent.handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}
